import { useState } from "react";
import { Truck, Calculator, Users, Clock, Shield, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import QuoteModal from "@/components/QuoteModal";
import SEO from "@/components/SEO";

const HERO_IMAGE = "https://images.pexels.com/photos/7931/pexels-photo-7931.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const services = [
  {
    icon: Truck,
    title: "Livraison",
    description: "Service de livraison rapide et fiable sur tout le territoire sénégalais. Flotte de camions adaptée à tous types de chantiers.",
    features: [
      "Livraison partout au Sénégal et Gambie",
      "Camions benne et semi-remorques",
      "Ponctualité garantie",
    ],
  },
  {
    icon: Calculator,
    title: "Devis Personnalisé",
    description: "Obtenez un devis détaillé et adapté à votre projet. Notre équipe évalue vos besoins pour vous proposer la meilleure offre.",
    features: [
      "Étude gratuite de votre projet",
      "Prix compétitifs",
      "Tarifs dégressifs selon volume",
      "Réponse sous 24h",
    ],
  },
  {
    icon: Users,
    title: "Conseil Technique",
    description: "Notre équipe d'experts vous accompagne dans le choix des matériaux adaptés à votre projet de construction.",
    features: [
      "Recommandations personnalisées",
      "Choix des granulométries",
      "Calcul des quantités",
      "Support technique continu",
    ],
  },
  {
    icon: Clock,
    title: "Disponibilité",
    description: "Stock permanent de tous nos produits pour répondre à vos besoins urgents et planifiés.",
    features: [
      "Large stock disponible",
      "Production continue",
      "Commandes urgentes acceptées",
      "Planification des livraisons",
    ],
  },
  {
    icon: Shield,
    title: "Qualité Garantie",
    description: "Tous nos matériaux sont soumis à des contrôles qualité rigoureux pour garantir leur conformité.",
    features: [
      "Contrôle qualité systématique",
      "Conformité aux normes BTP",
      "Traçabilité des produits",
      "Garantie satisfaction",
    ],
  },
  {
    icon: MapPin,
    title: "Proximité",
    description: "Deux sites de production stratégiquement situés pour une couverture optimale de la région.",
    features: [
      "Site agglos à Thiès",
      "Site granulats à Darou Khoudoss",
      "Réduction des coûts de transport",
      "Service de proximité",
    ],
  },
];

export default function ServicesPage() {
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  return (
    <div className="min-h-screen" data-testid="services-page">
      <SEO 
        title="Services - Livraison Agglos, Parpaings, Granulats"
        description="Services AGRABAT: livraison d'agglos, parpaings et granulats partout au Sénégal et Gambie. Devis gratuit, conseil technique, disponibilité garantie."
        keywords="livraison agglo Sénégal, transport parpaing, devis béton gratuit, fournisseur granulats Thiès, service BTP Dakar"
        url="https://agrabat.sn/services"
      />
      {/* Hero Section */}
      <section
        className="relative h-[50vh] flex items-center justify-center"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="services-hero"
      >
        <div className="absolute inset-0 bg-[#0A1320]/80" />
        <div className="relative z-10 text-center px-4">
          <p className="text-[#FFD100] font-medium uppercase tracking-widest mb-4">
            Ce que nous offrons
          </p>
          <h1 className="font-oswald text-5xl md:text-7xl font-bold text-white uppercase tracking-tight">
            Nos Services
          </h1>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white" data-testid="services-grid-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FFD100] font-medium uppercase tracking-widest mb-4">
              Solutions Complètes
            </p>
            <h2 className="font-oswald text-4xl md:text-5xl font-bold text-[#1E3A5F] uppercase tracking-tight">
              Un Accompagnement<br />Sur Mesure
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group bg-slate-50 border-l-4 border-[#1E3A5F] hover:border-[#FFD100] p-8 transition-all duration-300 hover:bg-white hover:shadow-xl"
                data-testid={`service-card-${index}`}
              >
                <service.icon className="w-12 h-12 text-[#FFD100] mb-6" />
                <h3 className="font-oswald text-2xl font-semibold text-[#1E3A5F] uppercase mb-4">
                  {service.title}
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="w-1.5 h-1.5 bg-[#FFD100] mt-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-slate-50" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FFD100] font-medium uppercase tracking-widest mb-4">
              Comment ça marche
            </p>
            <h2 className="font-oswald text-4xl md:text-5xl font-bold text-[#1E3A5F] uppercase tracking-tight">
              Notre Processus
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Contact", desc: "Décrivez votre projet et vos besoins" },
              { step: "02", title: "Évaluation", desc: "Nous analysons vos besoins" },
              { step: "03", title: "Devis", desc: "Recevez une offre détaillée" },
              { step: "04", title: "Livraison", desc: "Matériaux livrés sur site" },
            ].map((item, index) => (
              <div key={index} className="text-center" data-testid={`process-step-${index}`}>
                <div className="inline-flex items-center justify-center w-20 h-20 bg-[#1E3A5F] text-[#FFD100] font-oswald text-3xl font-bold mb-6">
                  {item.step}
                </div>
                <h3 className="font-oswald text-xl font-semibold text-[#1E3A5F] uppercase mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#1E3A5F]" data-testid="services-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-oswald text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6">
            Besoin de Matériaux ?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour discuter de votre projet et obtenir un devis personnalisé.
          </p>
          <Button
            onClick={() => setIsQuoteModalOpen(true)}
            className="bg-[#FFD100] text-[#1E3A5F] hover:bg-[#E6BC00] rounded-none px-8 py-6 text-lg font-bold uppercase tracking-wider"
            data-testid="services-quote-btn"
          >
            Demander un Devis Gratuit
          </Button>
        </div>
      </section>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} />
    </div>
  );
}
